import Geohash from 'latlon-geohash'
import { typesenseClient } from '~/client/common/typesense/client'
import type { ListSpacesParams } from '~/shared/space/space-list.params'
import { toKV, PrivacyChoices } from '~/shared/common/choices'
import { getAllSpaceIdsFromTypesense } from './space-list.api'

type VenueEnrichment = {
  blurb_bullets?: string[]
  blurb_bullets_sources?: { title: string; date: string; link: string; snippet: string }[]
  ai_filter_tags?: string[]
}

/**
 * Fetch venue enrichment data (blurb_bullets, blurb_bullets_sources, ai_filter_tags)
 * from the spaces collection for given space IDs.
 * The images collection doesn't store these venue fields.
 */
const fetchVenueEnrichment = async (spaceIds: number[]): Promise<Map<number, VenueEnrichment>> => {
  if (spaceIds.length === 0) return new Map()

  const venueFields = ['ai_filter_tags', 'blurb_bullets', 'blurb_bullets_sources']
  const BATCH_SIZE = 250
  const batches = Math.ceil(spaceIds.length / BATCH_SIZE)

  const searches = Array.from({ length: batches }, (_, i) => {
    const batchIds = spaceIds.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
    return {
      collection: 'spaces' as const,
      q: '*',
      filter_by: `id:=[${batchIds.join(',')}]`,
      per_page: BATCH_SIZE,
      include_fields: `id,$venues(${venueFields.join(',')})`,
    }
  })

  const response = await typesenseClient.multiSearch.perform({ searches })

  const enrichmentMap = new Map<number, VenueEnrichment>()
  for (const result of response.results) {
    const hits = (result as any)?.hits ?? []
    for (const hit of hits) {
      const doc = hit.document
      const id = Number(doc?.id)
      if (!Number.isFinite(id)) continue
      const joinedVenue = doc?.venues
      enrichmentMap.set(id, {
        blurb_bullets: joinedVenue?.blurb_bullets,
        blurb_bullets_sources: joinedVenue?.blurb_bullets_sources,
        ai_filter_tags: joinedVenue?.ai_filter_tags,
      })
    }
  }

  return enrichmentMap
}

const COLLECTION_NAME = 'images'
const DEFAULT_PAGE_SIZE = 20
const DEFAULT_QUERY_FIELDS = ['space.name', 'venue.name', 'image.caption', 'image.filename']
const DEFAULT_SORT = 'space.priority_sort_score:desc,biblio_space_id:asc'

export interface TypesenseSpaceListOptions {
  signal?: AbortSignal
  countOnly?: boolean
}

export interface TypesenseSpaceDocument {
  name: string
  id: number
  standing_capacity: number
  seated_capacity: number
  privacy_type: Record<string, string> | undefined
  venue: {
    name: string
    cuisines: any[]
    neighborhood: any
    city: string
    display_address: string
    address_1: string
    price_point: number
    rating_google: number
    rating_google_count: number
    michelin_rating: any
    date_opened: string
    is_permanently_closed: boolean
    is_temporarily_closed: boolean
    permanently_closed_aware_date?: string | null
    temporarily_closed_aware_date?: string | null
    create_source: string
    has_menus: boolean
    has_event_kit: boolean
    latitude: number
    longitude: number
    blurb_bullets?: string[]
    blurb_bullets_sources?: string[]
  }
  image: {
    url: string
    upload: string
    filename: string
    blurhash?: string
  }
  all_images: {
    url: string
    upload: string
    filename: string
    blurhash?: string
  }[]
  has_floorplans: boolean
  distance_from_point: number | null
}

export const getSpacesFromTypesenseVibeSearch = async (params: ListSpacesParams, options?: TypesenseSpaceListOptions): Promise<{ data: TypesenseSpaceDocument[]; count: number }> => {
  let embedding: number[] | undefined

  if (params.vibe_search) {
    try {
      const embeddingResponse = await fetch('/api/getEmbedding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: params.vibe_search }),
      })

      if (embeddingResponse.ok) {
        const embeddingData = (await embeddingResponse.json()) as { textEmbedding: number[] }
        embedding = embeddingData.textEmbedding
      } else {
        console.error('Failed to fetch embedding:', await embeddingResponse.text())
      }
    } catch (error) {
      console.error('Error fetching embedding:', error)
    }
  }

  // Pre-filter: if ai_filter_tags is present, get matching space IDs from the spaces collection
  // since the images collection doesn't have venue.ai_filter_tags indexed
  const aiFilterTags = params.ai_filter_tags && params.ai_filter_tags.length > 0 ? params.ai_filter_tags : undefined
  let preFilteredSpaceIds: number[] | undefined
  if (aiFilterTags) {
    preFilteredSpaceIds = await getAllSpaceIdsFromTypesense({
      active_venue_group_id: params.active_venue_group_id,
      ai_filter_tags: aiFilterTags,
    })
    // If no spaces match the ai_filter_tags, return empty results early
    if (preFilteredSpaceIds.length === 0) {
      return { data: [], count: 0 }
    }
  }

  const searchParams = buildSearchParams(params, options, embedding, preFilteredSpaceIds)

  const passedAiFilterTags = aiFilterTags ?? []

  // Uses multiSearch (POST body) because vector_query embeddings exceed URL length limits
  // for single .search() GET requests
  const multiSearchResponse = await typesenseClient.multiSearch.perform({
    searches: [{ collection: COLLECTION_NAME, ...searchParams }],
  }, {}, { abortSignal: options?.signal })
  const searchResult = multiSearchResponse.results[0] as any

  // Collect space IDs from results to fetch venue enrichment data
  const resultSpaceIds: number[] = (searchResult.grouped_hits ?? [])
    .map((group: any) => {
      const raw = group.hits?.[0]?.document?.['space.id']
      const spaceId = Number(raw)
      return Number.isFinite(spaceId) ? spaceId : null
    })
    .filter((id: number | null): id is number => id !== null)

  // Fetch blurb_bullets, blurb_bullets_sources, ai_filter_tags from spaces collection
  const venueEnrichment = await fetchVenueEnrichment(resultSpaceIds)

  const spaces =
    searchResult.grouped_hits?.map((group: any) => {
      if (embedding) {
        group.hits.sort((a: any, b: any) => (a.vector_distance ?? 10) - (b.vector_distance ?? 10))
      }

      const space = group.hits[0]

      // Images except floorplans
      const images = group.hits
        .filter((hit: any) => !Array.isArray(hit.document['image.sub_category']) || !hit.document['image.sub_category'].includes('floorplan'))
        .map((hit: any) => ({
          url: hit.document['image.url'] || (hit.document['image.upload'] ? `https://revelstreet-media-production.s3.amazonaws.com/media/${hit.document['image.upload']}` : ''),
          upload: hit.document['image.upload'],
          filename: hit.document['image.filename'],
          blurhash: hit.document['image.blurhash'],
        }))
      // Floorplans
      const floorplans = group.hits
        .filter((hit: any) => Array.isArray(hit.document['image.sub_category']) && hit.document['image.sub_category'].includes('floorplan'))
        .map((hit: any) => ({
          url: hit.document['image.url'] || (hit.document['image.upload'] ? `https://revelstreet-media-production.s3.amazonaws.com/media/${hit.document['image.upload']}` : ''),
          upload: hit.document['image.upload'],
          filename: hit.document['image.filename'],
        }))

      // Menus
      const menus = group.hits
        .filter((hit: any) => Array.isArray(hit.document['image.sub_category']) && hit.document['image.sub_category'].includes('menu'))
        .map((hit: any) => ({
          url: hit.document['image.url'] || (hit.document['image.upload'] ? `https://revelstreet-media-production.s3.amazonaws.com/media/${hit.document['image.upload']}` : ''),
          upload: hit.document['image.upload'],
          filename: hit.document['image.filename'],
        }))

      // Event kits
      const eventKits = group.hits
        .filter((hit: any) => Array.isArray(hit.document['image.sub_category']) && hit.document['image.sub_category'].includes('event_kit'))
        .map((hit: any) => ({
          url: hit.document['image.url'] || (hit.document['image.upload'] ? `https://revelstreet-media-production.s3.amazonaws.com/media/${hit.document['image.upload']}` : ''),
          upload: hit.document['image.upload'],
          filename: hit.document['image.filename'],
        }))

      const hasMenu = menus.length > 0
      const hasEventKit = eventKits.length > 0
      const hasFloorplans = floorplans.length > 0

      const result = {
        name: space.document['space.name'],
        id: space.document['space.id'],
        standing_capacity: space.document['space.standing_capacity'],
        seated_capacity: space.document['space.seated_capacity'],
        privacy_type: toKV(space.document['space.privacy_type'], PrivacyChoices),
        slug: space.document['space.slug'],
        venue: {
          name: space.document['venue.name'],
          cuisines: space.document['venue.cuisines'],
          neighborhood: {
            id: space.document['venue.neighborhood.id'],
            slug: space.document['venue.neighborhood.slug'],
            name: space.document['venue.neighborhood.name'],
          },
          city: space.document['venue.city'],
          display_address: space.document['venue.display_address'],
          address_1: space.document['venue.address_1'],
          price_point: space.document['venue.price_point'],
          rating_google: space.document['venue.rating_google'],
          rating_google_count: space.document['venue.rating_google_count'],
          michelin_rating: space.document['venue.michelin_rating'],
          date_opened: space.document['venue.date_opened'],
          is_permanently_closed: space.document['venue.is_permanently_closed'] ?? space.document['venue.permanently_closed'] ?? false,
          is_temporarily_closed: space.document['venue.is_temporarily_closed'] ?? false,
          permanently_closed_aware_date: space.document['venue.permanently_closed_aware_date'] ?? null,
          temporarily_closed_aware_date: space.document['venue.temporarily_closed_aware_date'] ?? null,
          create_source: space.document['venue.create_source'],
          has_menu: hasMenu,
          has_event_kit: hasEventKit,
          has_floorplans: hasFloorplans,
          latitude: space.document['venue.latitude'],
          longitude: space.document['venue.longitude'],
          slug: space.document['venues']?.slug || space.document['venue.slug'],
          blurb_bullets: venueEnrichment.get(Number(space.document['space.id']))?.blurb_bullets ?? space.document['venue.blurb_bullets'],
          blurb_bullets_sources: venueEnrichment.get(Number(space.document['space.id']))?.blurb_bullets_sources ?? space.document['venue.blurb_bullets_sources'],
          ai_filter_tags: venueEnrichment.get(Number(space.document['space.id']))?.ai_filter_tags ?? passedAiFilterTags,
        },
        image: images[0],
        all_images: images,
        has_floorplans: hasFloorplans,
        has_menu: hasMenu,
        has_event_kit: hasEventKit,
        distance_from_point: space.document['distance_from_point'] ?? null,
      }

      return result
    }) ?? []

  return { data: spaces as any, count: searchResult.found }
}

const buildSearchParams = (params: ListSpacesParams, options?: TypesenseSpaceListOptions, embedding?: number[], preFilteredSpaceIds?: number[]) => {
  const page = toPositiveNumber(params.page) ?? 1
  const perPage = options?.countOnly ? 0 : DEFAULT_PAGE_SIZE
  const filterClauses = buildFilterClauses(params, preFilteredSpaceIds)
  const filterBy = filterClauses.length ? filterClauses.join(' && ') : undefined

  const searchParams: any = {
    q: buildQuery(params),
    query_by: DEFAULT_QUERY_FIELDS.join(','),
    page,
    per_page: perPage,
    filter_by: filterBy,
    sort_by: embedding ? undefined : DEFAULT_SORT,
    include_fields: [
      'space.name',
      'space.id',
      'space.standing_capacity',
      'space.seated_capacity',
      'space.privacy_type',
      'space.slug',
      'venue.name',
      'venue.cuisines.id',
      'venue.cuisines.slug',
      'venue.cuisines.name',
      'venue.neighborhood.id',
      'venue.neighborhood.slug',
      'venue.neighborhood.name',
      'venue.city',
      'venue.display_address',
      'venue.address_1',
      'venue.price_point',
      'venue.rating_google',
      'venue.rating_google_count',
      'venue.michelin_rating',
      'venue.date_opened',
      'venue.is_permanently_closed',
      'venue.is_temporarily_closed',
      'venue.permanently_closed',
      'venue.permanently_closed_aware_date',
      'venue.temporarily_closed_aware_date',
      'venue.create_source',
      'venue.slug',
      'venue.latitude',
      'venue.longitude',
      'venue.location',
      'venue.blurb_bullets',
      'venue.blurb_bullets_sources',
      'distance_from_point',
      'image.url',
      'image.upload',
      'image.filename',
      'image.category',
      'image.sub_category',
      'image.blurhash',
      '$venues(slug)', // JOIN to fetch slug from venues collection
    ].join(','),
    group_by: 'space_id',
    group_limit: 10,
    num_typos: 2,
    drop_tokens_threshold: 1,
  }

  if (embedding) {
    searchParams.vector_query = `image.vertex_embedding_vector:([${embedding.join(',')}], k:1000)`
  }

  return searchParams
}

const buildQuery = (params: ListSpacesParams): string => {
  const venueName = toTrimmedString(params.venue_name)
  if (venueName) return venueName

  return '*'
}

const buildFilterClauses = (params: ListSpacesParams, preFilteredSpaceIds?: number[]): string[] => {
  const clauses: string[] = []
  const addClause = (clause?: string) => {
    if (clause) clauses.push(clause)
  }

  addClause(buildEqualityClause('image.category', 'space_image'))

  const activeVenueGroupId = params.active_venue_group_id ?? 1
  addClause(buildEqualityClause('venue.venue_group.id', activeVenueGroupId))

  addClause(buildEqualityClause('venue.google_place_id', params.google_place_id))

  const neighborhoodIds = toStringArray(params.neighborhoods)
  addClause(buildMultiValueClause('venue.neighborhood.id', neighborhoodIds))

  const cuisineValues = toStringArray(params.cuisines)
  addClause(buildIdOrNameMultiValueClause('venue.cuisines.id', 'venue.cuisines.name', cuisineValues))

  const venueTypeIds = toStringArray(params.venue_types)
  addClause(buildMultiValueClause('venue.venue_types.id', venueTypeIds))

  const pricePoints = toStringArray(params.prices)
  addClause(buildMultiValueClause('venue.price_point', pricePoints))

  const michelinRatings = toStringArray(params.michelin_ratings)
  addClause(buildMultiValueClause('venue.michelin_rating', michelinRatings))

  const privacyFilters = toStringArray(params.privacy)
  addClause(buildMultiValueClause('space.privacy_type', privacyFilters))

  const noiseFilters = toStringArray(params.noise)
  addClause(buildMultiValueClause('space.noise_privacy', noiseFilters))

  const spaceTypes = toStringArray(params.space_types)
  addClause(buildMultiValueClause('space.space_types', spaceTypes))

  const outdoorSpaceTypes = toStringArray(params.outdoor_space_types)
  addClause(buildMultiValueClause('space.outdoor_space_type', outdoorSpaceTypes))

  const windowOptions = toStringArray(params.windows)
  addClause(buildMultiValueClause('space.windows', windowOptions))

  const amenities = toStringArray(params.amenities)
  addClause(buildMultiValueClause('space.amenities', amenities))

  const avOptions = toStringArray(params.av)
  addClause(buildMultiValueClause('space.space_media', avOptions))

  // ai_filter_tags: use pre-filtered space IDs from the spaces collection
  // (the images collection doesn't have venue.ai_filter_tags indexed)
  if (preFilteredSpaceIds && preFilteredSpaceIds.length > 0) {
    addClause(`space_id:=[${preFilteredSpaceIds.join(',')}]`)
  }

  if (typeof params.wheelchair === 'boolean') {
    addClause(`space.wheelchair_accesible:=${params.wheelchair}`)
  }

  const fnbFrom = toNumber(params.f_and_b_minimum_from)
  if (typeof fnbFrom === 'number') {
    addClause(`space.f_and_b_minimum:>=${fnbFrom}`)
  }

  const fnbTo = toNumber(params.f_and_b_minimum_to)
  if (typeof fnbTo === 'number') {
    addClause(`space.f_and_b_minimum:<=${fnbTo}`)
  }

  const minGoogleRating = toNumber(params.min_google_rating)
  if (typeof minGoogleRating === 'number') {
    addClause(`venue.rating_google:>=${minGoogleRating}`)
  }

  const standingGuestCount = toNumber(params.standing_guest_count)
  const seatedGuestCount = toNumber(params.seated_guest_count)
  const bothFiltered = typeof standingGuestCount === 'number' && typeof seatedGuestCount === 'number'
  const capacityClauses: string[] = []

  if (typeof standingGuestCount === 'number') {
    const { min, max } = capacityRange(standingGuestCount)
    if (bothFiltered) {
      capacityClauses.push(`(space.standing_capacity:>=${min} && space.standing_capacity:<=${max})`)
    } else {
      capacityClauses.push(`((space.standing_capacity:>=${min} && space.standing_capacity:<=${max}) || (space.assumed_capacity:>=${min} && space.assumed_capacity:<=${max}))`)
    }
  }

  if (typeof seatedGuestCount === 'number') {
    const { min, max } = capacityRange(seatedGuestCount)
    if (bothFiltered) {
      capacityClauses.push(`(space.seated_capacity:>=${min} && space.seated_capacity:<=${max})`)
    } else {
      capacityClauses.push(`((space.seated_capacity:>=${min} && space.seated_capacity:<=${max}) || (space.assumed_capacity:>=${min} && space.assumed_capacity:<=${max}))`)
    }
  }

  if (capacityClauses.length) {
    addClause(`(${capacityClauses.join(' || ')})`)
  }

  if (params.nw && params.se) {
    const boundingBoxClauses = buildBoundingBoxClauses(params.nw, params.se)
    boundingBoxClauses.forEach(addClause)
  }

  return clauses
}

const buildEqualityClause = (field: string, rawValue: string | number | null | undefined) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return undefined
  return `${field}:=${formatFilterValue(rawValue)}`
}

const buildMultiValueClause = (field: string, values: string[]) => {
  if (!values.length) return undefined
  const formatted = values.map(formatFilterValue).join(',')
  return `${field}:=[${formatted}]`
}

const isIntegerString = (value: string): boolean => /^\d+$/.test(value)

const buildIdOrNameMultiValueClause = (idField: string, nameField: string, values: string[]) => {
  if (!values.length) return undefined

  const idValues: string[] = []
  const nameValues: string[] = []
  for (const value of values) {
    if (isIntegerString(value)) idValues.push(value)
    else nameValues.push(value)
  }

  const clauses = [
    buildMultiValueClause(idField, idValues),
    buildMultiValueClause(nameField, nameValues),
  ].filter((clause): clause is string => typeof clause === 'string')

  if (clauses.length === 0) return undefined
  if (clauses.length === 1) return clauses[0]
  return `(${clauses.join(' || ')})`
}

const buildBoundingBoxClauses = (nw: string, se: string) => {
  try {
    const northWest = Geohash.decode(nw)
    const southEast = Geohash.decode(se)

    const minLat = Math.min(northWest.lat, southEast.lat)
    const maxLat = Math.max(northWest.lat, southEast.lat)
    const minLng = Math.min(northWest.lon, southEast.lon)
    const maxLng = Math.max(northWest.lon, southEast.lon)

    return [`venue.location:(${maxLat}, ${minLng}, ${maxLat}, ${maxLng}, ${minLat}, ${maxLng}, ${minLat}, ${minLng})`]
  } catch {
    return []
  }
}

const capacityRange = (value: number) => {
  const min = Math.max(0, Math.round(value * 0.85))
  const max = Math.max(min, Math.round(value * 1.8))
  return { min, max }
}

const toPositiveNumber = (value: unknown): number | undefined => {
  const num = toNumber(value)
  if (typeof num === 'number' && num > 0) {
    return Math.floor(num)
  }
  return undefined
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return undefined
}

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value.map((entry) => toTrimmedString(entry)).filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : undefined
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return undefined
}

const formatFilterValue = (value: string | number) => {
  if (typeof value === 'number') return value
  const sanitized = value.replace(/"/g, '\\"')
  return `"${sanitized}"`
}
